import { Component, Input, Inject } from '@angular/core';
import { BaseComponent } from '@stockaid/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AscV2Service } from '../../../../../core/services/v2/asc.service';
import { CompanyService } from '../../../../../core/services/company.service';
import { Company } from '../../../../../core/models/company';
import { SyncService } from '../../../../../core/services/sync.service';
import { ModalService, MODAL_DATA } from '@stockaid/services';
import { UpdateSPOCompanyModalComponent } from '../spo-company-modal/update-spo-company-modal.component';
import { NEVER } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { loadCalendlyPopupWidget } from '@stockaid/utils';
import { DynamicScriptsService } from 'src/app/core/services/shared/dynamic-scripts.service';

/**
 * Modal to guide users on enabling specific columns in their Amazon Seller Central All Listing Report.
 * Automatically triggered for sellers with > 1000 items who haven't verified their columns.
 */
@Component({
  selector: 'app-inventory-report-guidelines-modal',
  templateUrl: './inventory-report-guidelines-modal.component.html',
  styleUrls: ['./inventory-report-guidelines-modal.component.scss'],
})
export class InventoryReportGuidelinesModalComponent extends BaseComponent {
  @Input() itemCount: number;
  
  threshold = 0;
  verifying = false;

  isProcessing = false;
  header = '';
  message = '';
  progress = 0;
  currentCompany: Company;

  constructor(
    public activeModal: NgbActiveModal,
    private ascV2Service: AscV2Service,
    private companyService: CompanyService,
    private syncService: SyncService,
    private modalService: ModalService,
    private dynamicScriptService: DynamicScriptsService,
    @Inject(MODAL_DATA) public data
  ) {
    super();
    this.threshold = environment.guidelineModalMinRequiredItems;
    if (this.data && this.data.itemCount !== undefined) {
      this.itemCount = this.data.itemCount;
    }
  }

  ngOnInit(): void {
    this.startTimer();
    this.companyService.getCurrentCompany().pipe(this.autoCleanUp()).subscribe(company => {
      this.currentCompany = company;
    });

    this.dynamicScriptService
      .loadDynamicScript('https://assets.calendly.com/assets/external/widget.js')
      .subscribe();
  }

  onScheduleMeeting(): void {
    const Calendly = (window as any).Calendly;
    loadCalendlyPopupWidget(Calendly);
  }

  dismiss(): void {
    if (this.timerInterval) clearInterval(this.timerInterval);
    this.activeModal.dismiss('close');
  }

  ngOnDestroy(): void {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  countdown = environment.customReportButtonEnableDelaySeconds; // 3 minutes
  timerInterval: any;

  startTimer(): void {
    this.timerInterval = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        clearInterval(this.timerInterval);
      }
    }, 1000);
  }

  get formatTime(): string {
    const m = Math.floor(this.countdown / 60);
    const s = this.countdown % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  }

  sync(): void {
    if (!this.currentCompany?.companyKey) return;

    this.activeModal.close('sync_started');

    // Verify the ASC connection is valid before starting the background job
    this.syncService
      .ascTestConnection(this.currentCompany)
      .pipe(
        switchMap(({ isConnectionOK, shouldShowMaintenanceMessage }) => {
          if (!isConnectionOK && !shouldShowMaintenanceMessage) {
            // Connection is invalid, open the modal to prompt the user to update their credentials
            this.modalService.open(UpdateSPOCompanyModalComponent, {
              data: {
                company: this.currentCompany,
              },
              size: 'lg',
              backdrop: 'static',
              keyboard: false,
            });
          }

          if (!isConnectionOK || shouldShowMaintenanceMessage) {
            return NEVER;
          }

          // Trigger the backend job
          return this.ascV2Service.syncPricingListMaxMin(this.currentCompany.companyKey);
        })
      )
      .subscribe();
  }
}
